"use client";
import EmphasizedText from "@/components/EmphasizedText";
import Hero from "@/components/Hero";
import { ShortCodeHero, ShortCodeHeroProps } from "@/components/Hero/shortcode";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import MonarchResolver from "@/monarch/MonarchResolver";
import ProductSearch from "@/components/ProductSearch";
import { Skeleton } from "@/components/ui/skeleton";
import type { HeroPersonalizationV2TestResult } from "@/monarch/rules/heroPersonalizationV2/types";

function Loader() {
  return (
    <>
      <Skeleton className="h-8 w-96 mb-4 mt-10 rounded-none-sm bg-slate-100/20" />
      <Skeleton className="h-8 w-96 mb-4 rounded-none-sm bg-slate-100/20" />
      <Skeleton className="h-8 w-80 mb-32 rounded-none-sm bg-slate-100/20" />

      <Skeleton className="h-4 w-96 mb-4 rounded-none-sm bg-slate-100/20" />
      <Skeleton className="h-4 w-80 mb-4 rounded-none-sm bg-slate-100/20" />
      <Skeleton className="h-4 w-64 mb-4 rounded-none-sm bg-slate-100/20" />

      <div className="flex flex-row mt-10 space-x-4 mb-10">
        <Skeleton className="h-11 w-64 mb-4 rounded-none bg-slate-100/20" />
        <Skeleton className="h-11 w-28 mb-4 rounded-sm bg-slate-100/20" />
      </div>
    </>
  );
}

function HeroComponent(props: ShortCodeHeroProps) {
  return (
    <MonarchDataConsumer<HeroPersonalizationV2TestResult>
      experimentKey="useHeroPersonalizationV2"
      render={({ data }) => {
        if (!data) {
          return <ShortCodeHero {...props} />;
        }
        const image = {
          ...data.image,
          altText: data.image.altText ?? "",
        };
        const mobileImage = data.mobileImage
          ? {
              ...data.mobileImage,
              altText: data.mobileImage.altText ?? "",
            }
          : undefined;
        return (
          <Hero
            variant="bg-image"
            title={data.title}
            image={image}
            mobileImage={mobileImage}
          >
            <h1>
              <EmphasizedText
                text={data.title}
                textEmphasis={data.titleEmphasis}
                emphasisClass="text-accent-pink"
              />
            </h1>
            <p className="mb-12">{data.description}</p>
            <ProductSearch
              placeholder="Search our 4000+ courses"
              conversionType={data.eventing?.conversionType ?? "hero-search"}
            />
          </Hero>
        );
      }}
    />
  );
}

export default function HomepageHeroClient(props: ShortCodeHeroProps) {
  return (
    <MonarchResolver<HeroPersonalizationV2TestResult>
      experimentKey="useHeroPersonalizationV2"
      variants={{
        control: <ShortCodeHero {...props} />,
        homepageHeroV2Control: <HeroComponent {...props} />,
        homepageHeroV2VariantA: <HeroComponent {...props} />,
        homepageHeroV2VariantB: <HeroComponent {...props} />,
        homepageHeroV2VariantC: <HeroComponent {...props} />,
      }}
      Loader={<Loader />}
      fallbackVariant="control"
    />
  );
}
